import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useLanguage } from '../../contexts/LanguageContext';
import { journalEntriesService, JournalEntryDto } from '../../services/journalEntries.service';
import OpeningEntryDialog from '../../components/openingEntry/OpeningEntryDialog';

const translations = {
  en: {
    title: 'Opening Entries',
    newEntry: 'New Opening Entry',
    search: 'Search',
    entryNumber: 'Entry Number',
    date: 'Date',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    status: 'Status',
    description: 'Description',
    actions: 'Actions',
    draft: 'Draft',
    posted: 'Posted',
    post: 'Post',
    unpost: 'Unpost',
    edit: 'Edit',
    delete: 'Delete',
    preview: 'Preview',
    refresh: 'Refresh',
    noData: 'No opening entries found',
    deleteConfirm: 'Are you sure you want to delete this entry?',
    postConfirm: 'Are you sure you want to post this entry?',
    unpostConfirm: 'Are you sure you want to unpost this entry?',
    success: 'Operation completed successfully',
    error: 'An error occurred',
    totalEntries: 'Total Entries',
    draftEntries: 'Draft',
    postedEntries: 'Posted',
    totalAmount: 'Total Amount',
    cancel: 'Cancel',
    confirm: 'Confirm',
  },
  ar: {
    title: 'القيود الافتتاحية',
    newEntry: 'قيد افتتاحي جديد',
    search: 'بحث',
    entryNumber: 'رقم القيد',
    date: 'التاريخ',
    totalDebit: 'إجمالي المدين',
    totalCredit: 'إجمالي الدائن',
    status: 'الحالة',
    description: 'الوصف',
    actions: 'الإجراءات',
    draft: 'مسودة',
    posted: 'مرحّل',
    post: 'ترحيل',
    unpost: 'إلغاء الترحيل',
    edit: 'تعديل',
    delete: 'حذف',
    preview: 'معاينة',
    refresh: 'تحديث',
    noData: 'لا توجد قيود افتتاحية',
    deleteConfirm: 'هل أنت متأكد من حذف هذا القيد؟',
    postConfirm: 'هل أنت متأكد من ترحيل هذا القيد؟',
    unpostConfirm: 'هل أنت متأكد من إلغاء ترحيل هذا القيد؟',
    success: 'تمت العملية بنجاح',
    error: 'حدث خطأ',
    totalEntries: 'إجمالي القيود',
    draftEntries: 'مسودات',
    postedEntries: 'مرحّلة',
    totalAmount: 'إجمالي المبالغ',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
  },
};

const OpeningEntryPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = translations[language];

  const [entries, setEntries] = useState<JournalEntryDto[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<JournalEntryDto | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    totalAmount: 0
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadEntries();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [entries, searchTerm]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      // جلب القيود ثم فلترة بنوع OE فقط
      const data = await journalEntriesService.getAll();
      const openingEntries = data.filter((e: JournalEntryDto) => e.entryType === 'OE');
      setEntries(openingEntries);
    } catch (err: any) {
      showSnackbar(t.error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...entries];
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        e.entryNumber.toLowerCase().includes(search) ||
        (e.description && e.description.toLowerCase().includes(search))
      );
    }
    setFilteredEntries(filtered);
  };

  const calculateStats = () => {
    const total = entries.length;
    const draft = entries.filter(e => e.status === 'Draft').length;
    const posted = entries.filter(e => e.status === 'Posted').length;
    const totalAmount = entries.reduce((sum, e) => sum + e.totalDebit, 0);
    setStats({ total, draft, posted, totalAmount });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAdd = () => {
    setEditEntry(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(isRTL ? 'لا يمكن تعديل قيد مرحّل' : 'Cannot edit posted entry', 'error');
      return;
    }
    setEditEntry(entry);
    setDialogOpen(true);
  };

  const handleDelete = (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(isRTL ? 'لا يمكن حذف قيد مرحّل' : 'Cannot delete posted entry', 'error');
      return;
    }
    setConfirmDialog({
      open: true,
      title: t.delete,
      message: t.deleteConfirm,
      onConfirm: async () => {
        try {
          await journalEntriesService.delete(entry.journalEntryId);
          showSnackbar(t.success, 'success');
          loadEntries();
        } catch (err: any) {
          showSnackbar(err.response?.data?.message || t.error, 'error');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handlePost = (entry: JournalEntryDto) => {
    if (entry.status === 'Posted') {
      showSnackbar(isRTL ? 'القيد مرحّل بالفعل' : 'Entry already posted', 'info');
      return;
    }
    setConfirmDialog({
      open: true,
      title: t.post,
      message: t.postConfirm,
      onConfirm: async () => {
        try {
          await journalEntriesService.post(entry.journalEntryId);
          showSnackbar(t.success, 'success');
          loadEntries();
        } catch (err: any) {
          showSnackbar(err.response?.data?.message || t.error, 'error');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handleUnpost = (entry: JournalEntryDto) => {
    if (entry.status === 'Draft') {
      showSnackbar(isRTL ? 'القيد غير مرحّل' : 'Entry not posted', 'info');
      return;
    }
    setConfirmDialog({
      open: true,
      title: t.unpost,
      message: t.unpostConfirm,
      onConfirm: async () => {
        try {
          await journalEntriesService.unpost(entry.journalEntryId);
          showSnackbar(t.success, 'success');
          loadEntries();
        } catch (err: any) {
          showSnackbar(err.response?.data?.message || t.error, 'error');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditEntry(undefined);
  };

  const handleSuccess = () => {
    showSnackbar(t.success, 'success');
    loadEntries();
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalEntries}</Typography>
                </Box>
                <PlaylistAddCheckIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.draft}</Typography>
                  <Typography variant="body2">{t.draftEntries}</Typography>
                </Box>
                <PendingActionsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.posted}</Typography>
                  <Typography variant="body2">{t.postedEntries}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.totalAmount.toFixed(3)}</Typography>
                  <Typography variant="body2">{t.totalAmount}</Typography>
                </Box>
                <AccountBalanceWalletIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PlaylistAddCheckIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton color="primary" onClick={loadEntries} sx={{ bgcolor: 'rgba(102,126,234,0.1)', '&:hover': { bgcolor: 'rgba(102,126,234,0.2)' } }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}
              sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)', '&:hover': { background: 'linear-gradient(45deg, #5a6fd6 30%, #6a4190 90%)' } }}>
              {t.newEntry}
            </Button>
          </Stack>
        </Box>

        {/* Search */}
        <TextField
          label={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          placeholder={isRTL ? 'رقم القيد أو الوصف' : 'Entry number or description'}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.entryNumber}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.date}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.totalDebit}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.totalCredit}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.description}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress sx={{ color: '#667eea' }} />
                </TableCell>
              </TableRow>
            ) : filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t.noData}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => {
                const isDraft = entry.status === 'Draft';
                return (
                  <TableRow key={entry.journalEntryId} hover sx={{ '&:hover': { bgcolor: 'rgba(102,126,234,0.05)' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="#667eea">{entry.entryNumber}</Typography>
                    </TableCell>
                    <TableCell>{new Date(entry.entryDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main">{entry.totalDebit.toFixed(3)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main">{entry.totalCredit.toFixed(3)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={isDraft ? t.draft : t.posted} color={isDraft ? 'warning' : 'success'} size="small" sx={{ fontWeight: 'bold' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{entry.description || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.preview}>
                          <IconButton size="small" sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102,126,234,0.1)' } }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isDraft && (
                          <>
                            <Tooltip title={t.edit}>
                              <IconButton size="small" onClick={() => handleEdit(entry)} sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25,118,210,0.1)' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.delete}>
                              <IconButton size="small" onClick={() => handleDelete(entry)} sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.post}>
                              <IconButton size="small" onClick={() => handlePost(entry)} sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(46,125,50,0.1)' } }}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {!isDraft && (
                          <Tooltip title={t.unpost}>
                            <IconButton size="small" onClick={() => handleUnpost(entry)} sx={{ color: 'warning.main', '&:hover': { bgcolor: 'rgba(237,108,2,0.1)' } }}>
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <OpeningEntryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleSuccess}
        entry={editEntry}
      />

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>{t.cancel}</Button>
          <Button onClick={confirmDialog.onConfirm} variant="contained" color="primary">{t.confirm}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpeningEntryPage;